import { useState, useRef, useEffect } from "react";
import { Sparkles, Send, X, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/util";

const SUGGESTIONS = [
  "¿Por qué bajó nuestra calificación este mes?",
  "¿Qué hallazgos debería arreglar primero?",
  "¿Qué proveedores representan el mayor riesgo?",
  "Resume nuestra superficie de ataque en 3 oraciones.",
  "Explica el hallazgo Log4Shell y su impacto.",
];

const MODELS = [
  { provider: "anthropic", model: "claude-sonnet-4-6", label: "Claude Sonnet 4.6" },
  { provider: "openai", model: "gpt-5.2", label: "GPT-5.2" },
  { provider: "gemini", model: "gemini-3-flash-preview", label: "Gemini 3 Flash" },
];

export function AIAnalystPanel({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [model, setModel] = useState(MODELS[0]);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    const userMsgId = `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setMessages(m => [...m, { id: userMsgId, role: "user", content: q }]);
    setBusy(true);
    try {
      const r = await api.post("/ai/ask", { question: q, provider: model.provider, model: model.model });
      setMessages(m => [...m, {
        id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role: "assistant",
        content: r.data.answer,
        citations: r.data.citations || [],
        provider: r.data.provider,
        modelName: r.data.model,
        confidence: r.data.confidence,
      }]);
    } catch (e) {
      setMessages(m => [...m, {
        id: `a-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        role: "assistant",
        content: "Falló la solicitud a la IA: " + (e?.response?.data?.detail || e.message),
      }]);
    } finally { setBusy(false); }
  };

  return (
    <div
      className={cn(
        "fixed inset-y-0 right-0 z-40 w-full sm:w-[440px] bg-card/95 backdrop-blur-xl border-l border-border flex flex-col transition-transform duration-300",
        open ? "translate-x-0" : "translate-x-full"
      )}
      data-testid="ai-analyst-panel"
    >
      <div className="border-b border-border p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <div>
            <div className="font-display text-sm font-medium">Analista IA</div>
            <div className="font-mono text-[10px] text-muted-foreground tracking-wider uppercase">Copiloto · solo lectura</div>
          </div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-muted" data-testid="close-ai-panel">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-3 border-b border-border flex items-center gap-2 flex-wrap">
        {MODELS.map(m => (
          <button
            key={m.label}
            onClick={() => setModel(m)}
            className={cn(
              "font-mono text-[10px] tracking-widest uppercase px-2 py-1 border",
              model.label === m.label ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"
            )}
            data-testid={`ai-model-${m.provider}`}
          >{m.label}</button>
        ))}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div>
            <div className="overline mb-3">Suggested prompts</div>
            <div className="space-y-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s}
                  onClick={() => send(s)}
                  className="w-full text-left border border-border hover:border-primary hover:bg-primary/5 p-3 font-mono text-xs transition-colors"
                  data-testid={`ai-suggestion-${s.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <Zap className="w-3 h-3 inline mr-2 text-primary" />{s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
            <div className={cn(
              "max-w-[85%] px-3 py-2 border text-sm whitespace-pre-wrap",
              m.role === "user" ? "border-primary/50 bg-primary/10" : "border-border bg-background"
            )}>
              {m.content}
              {m.citations?.length > 0 && (
                <div className="mt-3 pt-2 border-t border-border">
                  <div className="overline mb-1">Evidence</div>
                  <div className="font-mono text-[10px] text-muted-foreground break-all">
                    {m.citations.map(c => c.id).join(", ")}
                  </div>
                </div>
              )}
              {m.provider && (
                <div className="mt-2 font-mono text-[9px] text-muted-foreground/60 tracking-wider uppercase">
                  {m.provider}/{m.modelName} · conf {(m.confidence * 100 | 0)}%
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="border border-border px-3 py-2 font-mono text-xs text-muted-foreground animate-pulse">
              Analyzing evidence...
            </div>
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="border-t border-border p-3 flex items-center gap-2"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask the analyst..."
          className="flex-1 bg-background border border-border px-3 py-2 text-sm font-mono focus:border-primary outline-none"
          data-testid="ai-input"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          className="border border-primary bg-primary/10 text-primary px-3 py-2 hover:bg-primary/20 disabled:opacity-40"
          data-testid="ai-send"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
